import * as TabsPrimitive from "@radix-ui/react-tabs"
import * as React from "react"

import { cn } from "~/utils/helpers"

import type { JSX } from "react"

const TabsRoot = TabsPrimitive.Root

/** Whether the enclosing `TabsList` paints the active pill, so triggers skip their own. */
const TabsIndicatorContext = React.createContext(false)

export type PillRect = { x: number; y: number; width: number; height: number }

const samePillRect = (a: PillRect | null, b: PillRect | null) =>
  a?.x === b?.x && a?.y === b?.y && a?.width === b?.width && a?.height === b?.height

/**
 * Radix doesn't expose the active value to the list, so follow the triggers' `data-state`.
 * `activeSelector` finds the active trigger; `SegmentedControl` passes its radio items'.
 */
export function useActivePillRect(
  listRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  activeSelector = '[role="tab"][data-state="active"]',
) {
  const [rect, setRect] = React.useState<PillRect | null>(null)

  React.useLayoutEffect(() => {
    const list = listRef.current
    if (!enabled || !list) return

    let observedTrigger: HTMLElement | null = null
    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => measure())

    function measure() {
      const trigger = list?.querySelector<HTMLElement>(activeSelector) ?? null
      if (trigger !== observedTrigger) {
        if (observedTrigger) resizeObserver?.unobserve(observedTrigger)
        if (trigger) resizeObserver?.observe(trigger)
        observedTrigger = trigger
      }
      const next =
        trigger && trigger.offsetWidth > 0
          ? {
              x: trigger.offsetLeft,
              y: trigger.offsetTop,
              width: trigger.offsetWidth,
              height: trigger.offsetHeight,
            }
          : null
      setRect((prev) => (samePillRect(prev, next) ? prev : next))
    }

    measure()
    resizeObserver?.observe(list)
    const mutationObserver = new MutationObserver(measure)
    mutationObserver.observe(list, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })
    return () => {
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
    }
  }, [listRef, enabled, activeSelector])

  return enabled ? rect : null
}

/** The track, the sliding pill and a trigger, shared with `SegmentedControl` so both look the same. */
export const PILL_TRACK_CLASS =
  "relative inline-flex gap-0.5 items-center justify-center rounded-[8px] bg-switch-off hover:bg-switch-off-hover p-0.5 shadow-switch-background"
export const PILL_CLASS =
  "pointer-events-none absolute left-0 top-0 rounded-[6px] bg-base shadow-switch-handle transition-[translate,width,height] duration-200 ease-overlay motion-reduce:transition-none"
export const PILL_TRIGGER_CLASS =
  "relative focus-visible:ring-ring hover:text-fg-base text-fg-subtle inline-flex items-center cursor-pointer justify-center whitespace-nowrap rounded-[6px] ring-offset-background transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 label-sm"

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    size?: "base" | "sm"
    /** Turn off when triggers restyle the active tab themselves. */
    indicator?: boolean
  }
>(({ className, size = "base", indicator = true, children, ...props }, ref) => {
  const listRef = React.useRef<HTMLDivElement>(null)
  React.useImperativeHandle(ref, () => listRef.current!)
  const pill = useActivePillRect(listRef, indicator)

  return (
    <TabsPrimitive.List
      ref={listRef}
      className={cn(PILL_TRACK_CLASS, size === "base" ? "h-8" : "h-7", className)}
      {...props}
    >
      {/* Mounted only once measured, so it doesn't slide in from 0,0. */}
      {pill && (
        <span
          aria-hidden
          className={PILL_CLASS}
          style={{
            width: pill.width,
            height: pill.height,
            translate: `${pill.x}px ${pill.y}px`,
          }}
        />
      )}
      <TabsIndicatorContext.Provider value={indicator}>{children}</TabsIndicatorContext.Provider>
    </TabsPrimitive.List>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    size?: "base" | "sm"
  }
>(({ className, size = "base", ...props }, ref) => {
  const listPaintsPill = React.useContext(TabsIndicatorContext)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        PILL_TRIGGER_CLASS,
        "data-[state=active]:text-fg-base",
        !listPaintsPill && "data-[state=active]:bg-base data-[state=active]:shadow-switch-handle",
        size === "base" ? "h-7 px-1.5 py-1" : "h-6 px-2 py-1.5",
        className,
      )}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "focus-visible:ring-ring mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export type Tab = {
  label: string
  isDefault?: boolean
  disabled?: boolean
  content: JSX.Element
  count?: number
}

type TabsProps = {
  tabs: Tab[]
  tabsListClassName?: string
  variant?: "default" | "notification"
  onValueChange?: (value: string) => void
}

const Tabs = ({ tabs, tabsListClassName, variant, onValueChange }: TabsProps) => {
  if (!tabs.length || !tabs[0]) return
  const defaultIndex = tabs.findIndex((x) => x.isDefault)
  const resolvedIndex = defaultIndex >= 0 ? defaultIndex : 0
  // @ts-ignore
  const defaultValue = tabs[resolvedIndex]?.label + resolvedIndex

  const showCount = () => {
    switch (variant) {
      case "notification":
        return false
      default:
        return false
    }
  }

  const notificationList =
    "bg-subtle hover:!bg-subtle text-base w-full justify-start rounded-none shadow-none border-b border-base px-4 pt-1 pb-0 hover:bg-transparent !shadow-none"
  const notificationTrigger =
    "!bg-transparent border-0 border-transparent border-b-[2px] data-[state=active]:border-b-fg-base rounded-none !shadow-none"
  const notificationContent = "mt-0 h-fit max-h-[575px]"

  return (
    <TabsRoot defaultValue={defaultValue} onValueChange={onValueChange}>
      <TabsList
        indicator={variant !== "notification"}
        className={cn(tabsListClassName, {
          [notificationList]: variant == "notification",
        })}
      >
        {tabs.map(({ label, count, disabled }, index) => (
          <TabsTrigger
            key={label + index}
            value={label + index}
            className={cn({ [notificationTrigger]: variant == "notification" }, "w-full")}
            disabled={disabled ?? false}
          >
            {label}
            {showCount() && count !== undefined && count > 0 && (
              <div className="flex ml-1 text-xs font-medium border border-fg-muted rounded-full min-w-4 px-1.5 justify-center items-center text-center">
                <div className="text-fg-muted">{count}</div>
              </div>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map(({ label, content }, index) => (
        <TabsContent
          key={"content-" + label + index}
          value={label + index}
          className={cn({ [notificationContent]: variant == "notification" })}
        >
          {content}
        </TabsContent>
      ))}
    </TabsRoot>
  )
}

export { Tabs, TabsRoot, TabsList, TabsTrigger, TabsContent }
